export type PlayTimeSample = {
  currentTime: number;
  duration: number;
  playbackRate?: number;
};

export type PlayTimeProgress = {
  watchedSeconds: number;
  percent: number;
  durationSeconds: number;
};

/**
 * Accumulates video time while the player is actually playing.
 * Seek jumps (large currentTime deltas) are ignored so scrubbing to the end
 * does not fill the watch bar.
 */
export function createPlayTimeAccumulator(intervalSeconds = 1) {
  let lastTime = 0;
  let watchedSeconds = 0;
  let initialized = false;

  function percentFor(duration: number): number {
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return Math.min(100, Math.floor((watchedSeconds / duration) * 100));
  }

  function snapshot(duration: number): PlayTimeProgress {
    const durationSeconds =
      Number.isFinite(duration) && duration > 0 ? duration : 0;
    return {
      watchedSeconds,
      percent: percentFor(durationSeconds),
      durationSeconds,
    };
  }

  return {
    seed(seconds: number) {
      if (!Number.isFinite(seconds) || seconds <= 0) return;
      watchedSeconds = Math.max(watchedSeconds, seconds);
    },
    markPosition(currentTime: number) {
      if (!Number.isFinite(currentTime)) return;
      lastTime = currentTime;
      initialized = true;
    },
    tick(sample: PlayTimeSample): PlayTimeProgress {
      const currentTime = Number.isFinite(sample.currentTime)
        ? sample.currentTime
        : 0;
      if (!initialized) {
        lastTime = currentTime;
        initialized = true;
        return snapshot(sample.duration);
      }
      const rate =
        sample.playbackRate && sample.playbackRate > 0 ? sample.playbackRate : 1;
      const delta = currentTime - lastTime;
      lastTime = currentTime;
      const maxDelta = 2 * intervalSeconds * rate;
      if (delta > 0 && delta <= maxDelta) {
        watchedSeconds += delta;
      }
      return snapshot(sample.duration);
    },
    getWatchedSeconds() {
      return watchedSeconds;
    },
  };
}
